import { useState } from 'react';
import type { Session } from '../backend/types.ts';
import { group } from '../lib/catalog.ts';
import { dateTime, fromLocalInput, longDate, relative, toLocalInput } from '../lib/format.ts';
import { newSession, useModel } from '../state.tsx';
import { BodyMap, HeatLegend } from '../ui/BodyMap.tsx';
import { Card, checkinChoice, CHECKIN_CHOICES, Pill } from '../ui/components.tsx';
import { LogSession } from './LogSession.tsx';

export function Today() {
  const model = useModel();
  const [logging, setLogging] = useState<Session | null>(null);
  const [choosing, setChoosing] = useState(false);
  const first = model.profile?.full_name.split(' ')[0] || 'there';
  const top = [...model.groupLoads].filter(([, l]) => l >= 0.4).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([g]) => group(g)?.name);
  const week = model.logged.filter((s) => new Date(s.started_at).getTime() > Date.now() - 7 * 86_400_000);
  const avg = week.filter((s) => s.intensity).map((s) => s.intensity!);

  return (
    <div className="screen">
      <div className="stack" style={{ gap: 4 }}>
        <span className="eyebrow">{longDate()}</span>
        <div className="row">
          <h1>Hi {first}</h1>
          <span className="spacer" />
          {model.backend.isDemo && <Pill text="Demo" color="var(--warning)" />}
        </div>
        {model.error && <p className="caption warning" role="alert">{model.error}</p>}
      </div>

      {choosing ? (
        <Card>
          <h2>What did you do?</h2>
          <button className="btn secondary" data-testid="log-trainer" onClick={() => { setLogging(newSession('trainer')); setChoosing(false); }}>Session with trainer</button>
          <button className="btn secondary" data-testid="log-solo" onClick={() => { setLogging(newSession('solo')); setChoosing(false); }}>Workout on my own</button>
          <button className="link" style={{ textAlign: 'center' }} onClick={() => setChoosing(false)}>Cancel</button>
        </Card>
      ) : (
        <button className="btn primary" data-testid="log-new" onClick={() => setChoosing(true)}>+ Log a session</button>
      )}

      <Card className="flush">
        <div style={{ padding: '16px 16px 0' }}>
          <h2>Last 7 days</h2>
          <p className="caption" data-testid="load-summary">{top.length ? `Most worked: ${top.join(', ')}` : 'Everything is fresh'}</p>
        </div>
        <BodyMap avatar={model.profile?.avatar ?? 'male'} mode={{ kind: 'heat', loads: model.loads }} />
        <div style={{ padding: '0 16px 16px' }}><HeatLegend /></div>
      </Card>

      {!model.todaysCheckin && (
        <Card>
          <div className="stack" role="group" aria-labelledby="checkin-title" style={{ gap: 12 }}>
            <h2 id="checkin-title">How does your body feel today?</h2>
            <div className="checkin">
              {CHECKIN_CHOICES.map((c) => (
                <button key={c.rating} type="button" data-testid={`checkin-${c.rating}`} style={{ borderColor: c.color }}
                  onClick={() => model.checkIn(c.rating)}>
                  <strong style={{ color: c.color }}>{c.title}</strong>
                  <span className="caption">{c.detail}</span>
                </button>
              ))}
            </div>
          </div>
        </Card>
      )}

      <NextSession />
      {!model.links.length && (
        <Card>
          <span className="eyebrow">Your trainer</span>
          <p>Link your trainer and they get a WhatsApp digest before each session: what you worked, how recovered you are, and your notes.</p>
          <button className="btn secondary" data-testid="connect-trainer" onClick={() => { location.hash = 'you'; scrollTo(0, 0); }}>Connect your trainer</button>
        </Card>
      )}

      {model.pinnedNotes[0] && (
        <Card>
          <div className="row caption">
            <span className="warning">📌 For your next session</span>
            <span className="spacer" />
            <span>{model.pinnedNotes[0].trainer_name}</span>
          </div>
          <p>{model.pinnedNotes[0].body}</p>
        </Card>
      )}

      <Card>
        <span className="eyebrow">This week</span>
        <div className="stats">
          <Stat value={String(week.length)} label="sessions" />
          <Stat value={String(week.filter((s) => s.kind === 'solo').length)} label="on your own" />
          <Stat value={avg.length ? (avg.reduce((a, b) => a + b, 0) / avg.length).toFixed(1) : '–'} label="avg intensity" />
          <Stat value={model.todaysCheckin ? checkinChoice(model.todaysCheckin.rating).short : '–'} label="feeling today" />
        </div>
      </Card>

      {logging && <LogSession session={logging} onClose={() => setLogging(null)} />}
    </div>
  );
}

function Stat({ value, label }: { value: string; label: string }) {
  return <div><div className="number">{value}</div><div className="caption">{label}</div></div>;
}

/**
 * The web app cannot read the calendar, so the trainee enters their next trainer session.
 * It drives the pre-session WhatsApp digest (sync_upcoming_sessions), like Calendar does on iOS.
 */
function NextSession() {
  const model = useModel();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  if (!model.consents.has('trainer_sharing') || !model.links.length) return null;
  const next = model.upcoming[0];

  const save = async (list: typeof model.upcoming) => {
    await model.setUpcoming(list.map(({ calendar_event_id, title, starts_at }) => ({ calendar_event_id, title, starts_at })));
  };

  return (
    <Card>
      <span className="eyebrow">Next session with your trainer</span>
      {!editing && next && (
        <div className="row">
          <div className="stack" style={{ gap: 2 }}>
            <h2 data-testid="next-session">{dateTime(next.starts_at)}</h2>
            <p className="caption">{relative(next.starts_at)} · {model.links[0].trainer_name} gets your digest about 2 hours before</p>
          </div>
          <span className="spacer" />
          <button className="link" onClick={() => save(model.upcoming.slice(1))}>Remove</button>
        </div>
      )}
      {!editing && model.upcoming.length > 1 && (
        <p className="caption">Then: {model.upcoming.slice(1, 4).map((u) => dateTime(u.starts_at)).join(' · ')}</p>
      )}
      {editing ? (
        <form className="stack" onSubmit={async (e) => {
          e.preventDefault();
          const starts_at = fromLocalInput(value);
          await save([...model.upcoming, { id: '', calendar_event_id: `web-${crypto.randomUUID()}`, title: 'Trainer session', starts_at }]
            .sort((a, b) => a.starts_at.localeCompare(b.starts_at)));
          setEditing(false);
        }}>
          <input className="field" type="datetime-local" value={value} min={toLocalInput(new Date().toISOString())}
            onChange={(e) => setValue(e.target.value)} aria-label="Session date and time" data-testid="next-session-input" />
          <div className="row">
            <button type="button" className="btn secondary" onClick={() => setEditing(false)}>Cancel</button>
            <button type="submit" className="btn primary" disabled={!value || new Date(value).getTime() < Date.now()} data-testid="next-session-save">Save</button>
          </div>
        </form>
      ) : (
        <button className="btn secondary" data-testid="next-session-add" onClick={() => {
          setValue(toLocalInput(new Date(Date.now() + 86_400_000).toISOString()).slice(0, 11) + '18:00');
          setEditing(true);
        }}>{next ? 'Add another session' : 'Add your next session'}</button>
      )}
    </Card>
  );
}
